import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { CATEGORIES } from '../../data/categories';

// Top 10 cities by population (slugs match backend /annonces/{slug} pages)
const TOP_CITIES = [
  { slug: 'luxembourg-city', name: { fr: 'Luxembourg-Ville', en: 'Luxembourg City', de: 'Luxemburg-Stadt', lb: 'Lëtzebuerg-Stad' } },
  { slug: 'esch-sur-alzette', name: { fr: 'Esch-sur-Alzette' } },
  { slug: 'differdange', name: { fr: 'Differdange' } },
  { slug: 'dudelange', name: { fr: 'Dudelange' } },
  { slug: 'petange', name: { fr: 'Pétange' } },
  { slug: 'hesperange', name: { fr: 'Hesperange' } },
  { slug: 'bettembourg', name: { fr: 'Bettembourg' } },
  { slug: 'mamer', name: { fr: 'Mamer' } },
  { slug: 'strassen', name: { fr: 'Strassen' } },
  { slug: 'mersch', name: { fr: 'Mersch' } },
];

const HEADINGS = {
  fr: { categories: 'Catégories populaires', cities: 'Villes populaires', help: 'Aide & Infos', discover: 'Découvrir', allCities: 'Toutes les villes →' },
  en: { categories: 'Popular Categories', cities: 'Popular Cities', help: 'Help & Info', discover: 'Discover', allCities: 'All cities →' },
  de: { categories: 'Beliebte Kategorien', cities: 'Beliebte Städte', help: 'Hilfe & Infos', discover: 'Entdecken', allCities: 'Alle Städte →' },
  lb: { categories: 'Populär Kategorien', cities: 'Populär Stied', help: 'Hëllef & Infoen', discover: 'Entdecken', allCities: 'All Stied →' },
};

const Footer = () => {
  const { t, language } = useLanguage();
  const lang = ['fr', 'en', 'de', 'lb'].includes(language) ? language : 'fr';
  const h = HEADINGS[lang];

  const catName = (c) => ({ en: c.nameEn, de: c.nameDe, lb: c.nameLb }[lang] || c.name);
  const cityName = (c) => c.name[lang] || c.name.fr;
  const topCategories = CATEGORIES.slice(0, 10);

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand section */}
          <div>
            <h2 className="text-2xl font-bold mb-4">
              <span className="text-emerald-400">Lebon</span>
              <span className="text-teal-400">Lux</span>
            </h2>
            <p className="text-gray-400 mb-4 text-sm leading-relaxed">
              {t('footer.aboutText')}
            </p>

            {/* Coverage area */}
            <div className="mt-6">
              <p className="text-xs text-gray-500 mb-2">{t('footer.serving')}</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-gray-800 rounded text-xs">{t('country.luxembourg')}</span>
                <span className="px-2 py-1 bg-gray-800 rounded text-xs">{t('country.germany')}</span>
                <span className="px-2 py-1 bg-gray-800 rounded text-xs">{t('country.france')}</span>
                <span className="px-2 py-1 bg-gray-800 rounded text-xs">{t('country.belgium')}</span>
              </div>
            </div>
          </div>

          {/* Top 10 categories (SEO internal linking) */}
          <div>
            <h3 className="text-white font-semibold mb-4">{h.categories}</h3>
            <ul className="space-y-2 text-sm" data-testid="footer-top-categories">
              {topCategories.map((cat) => (
                <li key={cat.id}>
                  <Link to={`/category/${cat.id}`} className="hover:text-emerald-400 transition-colors">
                    {catName(cat)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Top 10 cities (SEO internal linking) */}
          <div>
            <h3 className="text-white font-semibold mb-4">{h.cities}</h3>
            <ul className="space-y-2 text-sm" data-testid="footer-top-cities">
              {TOP_CITIES.map((city) => (
                <li key={city.slug}>
                  <Link to={`/annonces/${city.slug}`} className="hover:text-emerald-400 transition-colors">
                    {cityName(city)}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/villes" className="text-emerald-400 hover:text-emerald-300 transition-colors font-medium" data-testid="footer-all-cities-link">
                  {h.allCities}
                </Link>
              </li>
            </ul>
          </div>

          {/* Help & Info */}
          <div>
            <h3 className="text-white font-semibold mb-4">{h.help}</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/how-it-works" className="hover:text-emerald-400 transition-colors">{t('footer.howItWorks')}</Link></li>
              <li><Link to="/safety" className="hover:text-emerald-400 transition-colors">{t('footer.safety')}</Link></li>
              <li><Link to="/faq" className="hover:text-emerald-400 transition-colors">{t('footer.faq')}</Link></li>
              <li><Link to="/contact" className="hover:text-emerald-400 transition-colors">{t('footer.contact')}</Link></li>
              <li><Link to="/about" className="hover:text-emerald-400 transition-colors">{t('footer.aboutUs')}</Link></li>
              <li><Link to="/careers" className="hover:text-emerald-400 transition-colors">{t('footer.careers')}</Link></li>
            </ul>

            {/* SEO: High-value keyword links (multilingual via basename) */}
            <h3 className="text-white font-semibold mb-3 mt-6">{h.discover}</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/seo/marketplace-luxembourg" className="hover:text-emerald-400 transition-colors">Marketplace Luxembourg</Link></li>
              <li><Link to="/seo/petites-annonces-luxembourg" className="hover:text-emerald-400 transition-colors">Petites Annonces Luxembourg</Link></li>
              <li><Link to="/seo/immobilier-luxembourg" className="hover:text-emerald-400 transition-colors">Immobilier Luxembourg</Link></li>
              <li><Link to="/seo/voiture-occasion-luxembourg" className="hover:text-emerald-400 transition-colors">Voiture Occasion Luxembourg</Link></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
            <p className="text-gray-500">
              {t('footer.copyright', { year: new Date().getFullYear() })}
            </p>
            <div className="flex gap-6 text-gray-400">
              <Link to="/privacy" className="hover:text-emerald-400 transition-colors">{t('footer.privacy')}</Link>
              <Link to="/terms" className="hover:text-emerald-400 transition-colors">{t('footer.terms')}</Link>
              <Link to="/cookies" className="hover:text-emerald-400 transition-colors">{t('footer.cookies')}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
